import { useEffect, useMemo, useState } from 'react';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import { wizard, studies, offers } from '@/api/endpoints';
import type { StudySummary, VariantRecommendation, StudyType, Offer } from '@/types';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { formatMoney } from '@/lib/money';

export function ContractWizard() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const offerIdParam = params.get('offerId');
  const offerId = offerIdParam ? Number(offerIdParam) : null;

  const [types, setTypy] = useState<StudyType[]>([]);
  const [oprList, setOprList] = useState<StudySummary[]>([]);
  const [sourceOffer, setSourceOffer] = useState<Offer | null>(null);
  const [typeCode, setTypCode] = useState('');
  const [authority, setAuthority] = useState('DISTRICT');
  const [studyId, setStudyId] = useState<number | ''>(() => {
    const p = params.get('studyId');
    return p ? Number(p) : '';
  });
  const [etapowa, setEtapowa] = useState(false);
  const [pompowanie, setPompowanie] = useState(false);
  const [monitoring, setMonitoring] = useState(false);
  const [strefy, setStrefy] = useState(false);
  const [zamowieniePubliczne, setZamowieniePubliczne] = useState(false);
  const [amount, setAmount] = useState<string>('');
  const [variant, setVariant] = useState<string>('');
  const [rek, setRek] = useState<VariantRecommendation | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Lista opracowań filtrowana do tych powiązanych z ujęciem z oferty (gdy ?offerId=).
  const oprListFiltered = useMemo(() => {
    if (!sourceOffer || !sourceOffer.intakeId) return oprList;
    return oprList.filter(o => o.intakeId === sourceOffer.intakeId);
  }, [oprList, sourceOffer]);

  useEffect(() => {
    Promise.all([studies.types(), studies.list()])
      .then(([t, o]) => {
        setTypy(t);
        setOprList(o);
        if (t.length > 0 && !typeCode) setTypCode(t[0].code);
      })
      .catch(e => setErr(String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-fill z oferty: amount, autoselect study z odpowiedniego intake, ustaw typeCode i authority.
  useEffect(() => {
    if (!offerId) return;
    offers.get(offerId)
      .then(of => {
        setSourceOffer(of);
        if (of.totalGross != null) setAmount(String(of.totalGross));
        if (of.client?.id) {
          studies.list(undefined, undefined, of.client.id).then(forClient => {
            const sameIntake = of.intakeId
              ? forClient.filter(s => s.intakeId === of.intakeId)
              : forClient;
            if (sameIntake.length === 1) setStudyId(sameIntake[0].id);
            else if (sameIntake.length > 1 && !studyId) setStudyId(sameIntake[0].id);
            // Pre-fill type/authority na podstawie wybranego studium
            const picked = sameIntake[0];
            if (picked) {
              setTypCode(picked.typeCode);
              setAuthority(picked.authority);
            }
          }).catch(() => { /* nonfatal */ });
        }
      })
      .catch(e => setErr('Nie udało się wczytać oferty: ' + (e?.message ?? String(e))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId]);

  const odpowiedzi = useMemo(() => ({
    etapowaRealizacja: etapowa,
    pompowanie,
    monitoring,
    strefy,
    zamowieniePubliczne,
  }), [etapowa, pompowanie, monitoring, strefy, zamowieniePubliczne]);

  useEffect(() => {
    if (!typeCode || !authority) return;
    const h = setTimeout(() => {
      wizard.recommendation(typeCode, authority, odpowiedzi)
        .then(r => {
          setRek(r);
          if (!variant || !r.variants.some(v => v.variant === variant && v.available)) {
            setVariant(r.recommended);
          }
        })
        .catch(e => setErr(String(e)));
    }, 250);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeCode, authority, odpowiedzi]);

  const submit = () => {
    if (!studyId) { setErr('Wybierz opracowanie'); return; }
    if (!variant) { setErr('Wybierz variant'); return; }
    setBusy(true);
    setErr(null);
    const payload = {
      studyId: Number(studyId),
      variant,
      amount: amount ? Number(amount) : undefined,
      etapowaRealizacja: etapowa,
      pompowanie,
      monitoring,
      strefy,
      zamowieniePubliczne,
      offerId: offerId ?? undefined,
    };
    fetch('/api/contract-wizard', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include',
    })
      .then(async r => {
        if (!r.ok) {
          const text = await r.text().catch(() => '');
          throw new Error(text || ('HTTP ' + r.status));
        }
        // Z oferty → wróć na ofertę (już CONVERTED, z linkiem do umowy). Bez oferty → na studium.
        if (offerId) nav('/oferty/' + offerId);
        else nav('/studies/' + studyId);
      })
      .catch(e => setErr(String(e)))
      .finally(() => setBusy(false));
  };

  return (
    <div className="max-w-[1600px]">
      <header className="flex items-end justify-between mb-5">
        <div>
          <h1>Kreator umów</h1>
          <div className="text-ink-400 text-[13px]">Dobierz wariant umowy na podstawie charakterystyki opracowania</div>
        </div>
      </header>

      {err && <div className="text-red-500 mb-3 text-[12px]">Błąd: {err}</div>}

      {sourceOffer && (
        <div className="card mb-4 border-water-500/30 bg-water-500/5">
          <div className="card-body py-3 flex items-center gap-3">
            <Icon name="file-text" className="w-5 h-5 text-water-600 shrink-0"/>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] text-ink-500 uppercase tracking-wider">Generujesz umowę z oferty</div>
              <div className="text-[14px] font-semibold text-ink-900">
                <NavLink to={`/oferty/${sourceOffer.id}`} className="text-water-600 no-underline hover:underline">
                  {sourceOffer.number}
                </NavLink>
                {' — '}
                {sourceOffer.title}
              </div>
              <div className="text-[11px] text-ink-500 mt-1">
                Klient: <span className="font-semibold">{sourceOffer.client.name}</span>
                {sourceOffer.intakeName && <> · Ujęcie: <span className="font-semibold">{sourceOffer.intakeName}</span></>}
                {' · '}Wartość brutto z oferty:{' '}
                <span className="font-mono font-semibold">{formatMoney(sourceOffer.totalGross)} zł</span>
                {sourceOffer.paymentTerms && <> · {sourceOffer.paymentTerms}</>}
              </div>
              {oprListFiltered.length === 0 && (
                <div className="mt-2 text-[12px] text-amber-600">
                  ⚠ Dla tego ujęcia nie istnieje jeszcze opracowanie. Załóż je wcześniej, żeby powiązać umowę.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-[340px_1fr_320px] gap-4">
        <div className="card">
          <div className="card-head"><div className="card-title">Pytania</div></div>
          <div className="card-body flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <div className="text-[11px] font-semibold text-ink-600 uppercase tracking-wide">1 · Typ opracowania</div>
              <SearchableSelect value={typeCode} onChange={setTypCode}>
                {types.map(t => <option key={t.code} value={t.code}>{t.abbreviation}-{t.code} — {t.name}</option>)}
              </SearchableSelect>
            </div>
            <div className="flex flex-col gap-1">
              <div className="text-[11px] font-semibold text-ink-600 uppercase tracking-wide">2 · Decydent</div>
              <SearchableSelect value={authority} onChange={setAuthority}>
                <option value="DISTRICT">Starosta</option>
                <option value="REGIONAL">Marszałek</option>
              </SearchableSelect>
            </div>
            <div className="flex flex-col gap-1">
              <div className="text-[11px] font-semibold text-ink-600 uppercase tracking-wide">3 · Opracowanie</div>
              <SearchableSelect value={studyId === '' ? '' : String(studyId)} onChange={v => setStudyId(v ? Number(v) : '')}>
                <option value="">— wybierz —</option>
                {oprListFiltered.map(o => <option key={o.id} value={o.id}>{o.number} — {o.title}</option>)}
              </SearchableSelect>
              {sourceOffer && oprListFiltered.length < oprList.length && (
                <span className="text-[10px] text-ink-400 mt-1">Pokazane tylko opracowania dla ujęcia z oferty.</span>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <div className="text-[11px] font-semibold text-ink-600 uppercase tracking-wide">4 · Zakres prac</div>
              <label className="flex items-center gap-2 text-[12px] text-ink-700"><input type="checkbox" checked={etapowa} onChange={e => setEtapowa(e.target.checked)}/> Etapowa realizacja</label>
              <label className="flex items-center gap-2 text-[12px] text-ink-700"><input type="checkbox" checked={pompowanie} onChange={e => setPompowanie(e.target.checked)}/> Próbne pompowanie</label>
              <label className="flex items-center gap-2 text-[12px] text-ink-700"><input type="checkbox" checked={monitoring} onChange={e => setMonitoring(e.target.checked)}/> Monitoring</label>
              <label className="flex items-center gap-2 text-[12px] text-ink-700"><input type="checkbox" checked={strefy} onChange={e => setStrefy(e.target.checked)}/> Strefy ochronne</label>
            </div>
            <div className="flex flex-col gap-1">
              <div className="text-[11px] font-semibold text-ink-600 uppercase tracking-wide">5 · Tryb zamówienia</div>
              <label className="flex items-center gap-2 text-[12px] text-ink-700"><input type="checkbox" checked={zamowieniePubliczne} onChange={e => setZamowieniePubliczne(e.target.checked)}/> Zamówienie publiczne (PZP)</label>
            </div>
            <div className="flex flex-col gap-1">
              <div className="text-[11px] font-semibold text-ink-600 uppercase tracking-wide">6 · Kwota</div>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)} className="input font-mono" placeholder="PLN"/>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head"><div className="card-title">Warianty umowy</div>{rek && <span className="ml-auto text-[11px] text-ink-400">rekomendacja: <span className="font-mono font-semibold text-ink-700">{rek.recommended}</span></span>}</div>
          <div className="card-body">
            {!rek && <div className="text-ink-400 text-[12px]">Obliczanie rekomendacji…</div>}
            {rek && rek.variants.map(v => {
              const isSelected = variant === v.variant;
              const isRek = v.variant === rek.recommended;
              return (
                <label
                  key={v.variant}
                  className={'flex items-start gap-3 p-3 mb-2 border rounded-lg cursor-pointer transition ' +
                    (!v.available ? 'opacity-40 cursor-not-allowed ' : '') +
                    (isSelected ? 'border-water-500 bg-water-500/5 ' : 'border-ink-200 hover:border-ink-300 ') +
                    (isRek ? 'ring-1 ring-ai-500/30' : '')}
                >
                  <input
                    type="radio"
                    name="variant"
                    disabled={!v.available}
                    checked={isSelected}
                    onChange={() => v.available && setVariant(v.variant)}
                    className="mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-ink-900">{v.displayName}</span>
                      {isRek && <span className="pill pill-ai">rekomendowany</span>}
                      {!v.available && <span className="pill pill-red">niedostępny</span>}
                    </div>
                    <div className="text-[11px] text-ink-500 font-mono">{v.variant} · {v.minPages}–{v.maxPages} stron</div>
                  </div>
                </label>
              );
            })}

            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => nav(-1)} className="btn btn-secondary">Anuluj</button>
              <button disabled={busy || !studyId || !variant} onClick={submit} className="btn btn-primary">
                {busy ? 'Zapisywanie…' : 'Utwórz umowę'}
              </button>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head"><div className="card-title">Cechy wariantu</div></div>
          <div className="card-body">
            {!rek && <div className="text-ink-400 text-[12px]">—</div>}
            {rek && rek.features.length === 0 && <div className="text-ink-400 text-[12px]">Brak cech.</div>}
            {rek && rek.features.map((c, i) => (
              <div key={i} className="flex items-start gap-2 py-1.5 text-[12px] text-ink-700">
                <Icon name="check" className="w-3.5 h-3.5 text-green-500 mt-0.5 shrink-0"/>
                <span>{c}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
